import type { AxiosRequestConfig } from 'axios';

/**
 * Checks if the URL path from an Axios request config starts with any of the provided path prefixes
 * Handles both absolute URLs (with protocol/domain) and relative paths
 *
 * @param config - Axios request config containing the URL
 * @param pathPrefixes - Array of path prefixes to check against (e.g., ['/api/v1', '/api/v2'])
 * @returns true if the path starts with any of the provided prefixes, false otherwise
 *
 * @example
 * matchesPathPrefixes({ url: '/api/v1/users' }, ['/api/v1']) // returns true
 * matchesPathPrefixes({ url: 'https://example.com/api/v1/users' }, ['/api/v1']) // returns true
 * matchesPathPrefixes({ url: '/api/v2/users' }, ['/api/v1', '/api/v2']) // returns true
 * matchesPathPrefixes({ url: '/other/path' }, ['/api/v1']) // returns false
 */
export function matchesPathPrefixes(
  config: AxiosRequestConfig,
  pathPrefixes: string[],
): boolean {
  if (!config.url || pathPrefixes.length === 0) return false;

  const isAbsolute = /^https?:\/\//.test(config.url);
  const fullUrl =
    !isAbsolute && config.baseURL
      ? `${config.baseURL.replace(/\/$/, '')}${config.url.startsWith('/') ? '' : '/'}${config.url}`
      : config.url;

  // Extract path from URL (handles both absolute and relative URLs)
  let path: string;
  try {
    // If URL is absolute (starts with http:// or https://), parse it
    if (fullUrl.startsWith('http://') || fullUrl.startsWith('https://')) {
      const url = new URL(fullUrl);
      path = url.pathname;
    } else {
      // If URL is relative, use it directly
      path = fullUrl;
    }
  } catch {
    // If URL parsing fails, fallback to using the URL string directly
    path = fullUrl;
  }

  // Check if path starts with any of the provided prefixes
  return pathPrefixes.some(prefix => path.startsWith(prefix));
}
